// Performance monitoring utilities for the virtualized member list
export class PerformanceMonitor {
    constructor() {
        this.metrics = new Map();
        this.observers = new Map();
        this.isEnabled = process.env.NODE_ENV === 'development' || window.location.search.includes('debug=performance');
    }

    // Start timing a performance metric
    startTiming(label) {
        if (!this.isEnabled) return;
        
        const startTime = performance.now();
        this.metrics.set(label, { startTime, type: 'timing' });
    }

    // End timing and log result
    endTiming(label) {
        if (!this.isEnabled) return;
        
        const metric = this.metrics.get(label);
        if (metric && metric.type === 'timing') {
            const duration = performance.now() - metric.startTime;
            console.log(`⏱️ Performance: ${label} took ${duration.toFixed(2)}ms`);
            this.metrics.set(label, { ...metric, duration, endTime: performance.now() });
        }
    }

    // Track render count
    trackRender(componentName) {
        if (!this.isEnabled) return;
        
        const key = `render_${componentName}`;
        const existing = this.metrics.get(key);
        const count = existing ? existing.count + 1 : 1;
        
        this.metrics.set(key, { count, type: 'render', lastRender: performance.now() });
        
        if (count % 10 === 0) {
            console.log(`🔄 Performance: ${componentName} has rendered ${count} times`);
        }
    }

    // Track memory usage (approximate)
    trackMemory(label) {
        if (!this.isEnabled || !performance.memory) return;
        
        const memory = {
            used: performance.memory.usedJSHeapSize,
            total: performance.memory.totalJSHeapSize,
            limit: performance.memory.jsHeapSizeLimit
        };
        
        console.log(`💾 Memory (${label}):`, {
            used: `${(memory.used / 1048576).toFixed(2)}MB`,
            total: `${(memory.total / 1048576).toFixed(2)}MB`,
            percentage: `${((memory.used / memory.total) * 100).toFixed(1)}%`
        });
    }

    // Monitor FPS during scrolling
    startFPSMonitoring(container) {
        if (!this.isEnabled) return;
        
        let frameCount = 0;
        let lastTime = performance.now();
        let animationId;
        
        const measureFPS = () => {
            frameCount++;
            const currentTime = performance.now();
            
            if (currentTime - lastTime >= 1000) {
                const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
                console.log(`📊 FPS: ${fps} frames per second`);
                frameCount = 0;
                lastTime = currentTime;
            }
            
            animationId = requestAnimationFrame(measureFPS);
        };
        
        const startMonitoring = () => {
            if (!animationId) {
                measureFPS();
            }
        };
        
        const stopMonitoring = () => {
            if (animationId) {
                cancelAnimationFrame(animationId);
                animationId = null;
            }
        };
        
        // Monitor during scroll
        container.addEventListener('scroll', startMonitoring);
        
        // Stop monitoring after scroll ends
        let scrollTimeout;
        container.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(stopMonitoring, 1000);
        });
        
        this.observers.set('fps', { startMonitoring, stopMonitoring });
        
        return () => {
            container.removeEventListener('scroll', startMonitoring);
            stopMonitoring();
        };
    }

    // Monitor DOM node count
    monitorDOMNodes(label = 'default') {
        if (!this.isEnabled) return;
        
        const nodeCount = document.querySelectorAll('*').length;
        console.log(`🏗️ DOM Nodes (${label}): ${nodeCount}`);
        return nodeCount;
    }

    // Track intersection observer performance
    trackIntersectionObserver(entries, observerName = 'default') {
        if (!this.isEnabled) return;
        
        const visible = entries.filter(entry => entry.isIntersecting).length;
        const total = entries.length;
        
        console.log(`👁️ Intersection Observer (${observerName}): ${visible}/${total} items visible`);
    }

    // Generate performance report
    getReport() {
        if (!this.isEnabled) return null;
        
        const report = {
            timestamp: new Date().toISOString(),
            metrics: Object.fromEntries(this.metrics),
            memory: performance.memory ? {
                used: performance.memory.usedJSHeapSize,
                total: performance.memory.totalJSHeapSize,
                limit: performance.memory.jsHeapSizeLimit
            } : null,
            timing: performance.timing ? {
                domComplete: performance.timing.domComplete - performance.timing.navigationStart,
                loadComplete: performance.timing.loadEventEnd - performance.timing.navigationStart
            } : null
        };
        
        console.table(this.metrics);
        return report;
    }

    // Clear all metrics
    clear() {
        this.metrics.clear();
        this.observers.clear();
    }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// React hook for component performance monitoring
export function usePerformanceMonitor(componentName) {
    const monitor = performanceMonitor;
    
    // Track render on every render
    monitor.trackRender(componentName);
    
    return {
        startTiming: (label) => monitor.startTiming(`${componentName}_${label}`),
        endTiming: (label) => monitor.endTiming(`${componentName}_${label}`),
        trackMemory: (label) => monitor.trackMemory(`${componentName}_${label}`),
        monitorFPS: (container) => monitor.startFPSMonitoring(container),
        getReport: () => monitor.getReport()
    };
}

// Utility to measure React component performance
export function withPerformanceMonitoring(WrappedComponent, componentName) {
    return function PerformanceMonitoredComponent(props) {
        const monitor = usePerformanceMonitor(componentName);
        
        // Measure render time
        monitor.startTiming('render');
        const result = WrappedComponent(props);
        monitor.endTiming('render');
        
        return result;
    };
}
